'use client'

import Image from 'next/image'
import { useState } from 'react'
import { cn } from '@nwwp/ui-shadcn'

// Red YouTube play button — matches Figma shape, no external icon dependency
const PlayButton = () => (
  <svg
    aria-hidden
    width="68"
    height="48"
    viewBox="0 0 68 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="drop-shadow-md"
  >
    <rect width="68" height="48" rx="12" fill="#FF0000" />
    <polygon points="26,14 50,24 26,34" fill="white" />
  </svg>
)

type Props = {
  videoId: string
  videoTitle: string
}

export const VideoEmbed = ({ videoId, videoTitle }: Props) => {
  const encodedVideoId = encodeURIComponent(videoId)
  const THUMBNAIL_URL = `https://img.youtube.com/vi/${encodedVideoId}/maxresdefault.jpg`
  const EMBED_URL = `https://www.youtube-nocookie.com/embed/${encodedVideoId}?autoplay=1`

  const [playing, setPlaying] = useState(false)

  if (playing) {
    return (
      <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-black">
        <iframe
          className="absolute inset-0 size-full"
          src={EMBED_URL}
          title={videoTitle}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    )
  }

  return (
    <button
      type="button"
      aria-label={`Play video: ${videoTitle}`}
      onClick={() => setPlaying(true)}
      className={cn(
        'group relative aspect-video w-full overflow-hidden rounded-lg',
        'cursor-pointer bg-neutral-surface',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none'
      )}
    >
      <Image
        src={THUMBNAIL_URL}
        alt={`${videoTitle} video thumbnail`}
        fill
        sizes="(max-width: 768px) 100vw, 542px"
        className="object-cover"
        unoptimized
      />

      {/* Dark overlay on hover */}
      <div aria-hidden className="absolute inset-0 bg-black/20 opacity-0 transition-opacity group-hover:opacity-100" />

      {/* Centered play button */}
      <div
        aria-hidden
        className={cn(
          'absolute inset-0 flex items-center justify-center',
          'transition-transform group-hover:scale-110'
        )}
      >
        <PlayButton />
      </div>
    </button>
  )
}
